import { useTranslation } from 'react-i18next';
import { ConsentType } from '__generated__/globalTypes';
import getSubdomainFromUrl from 'utils/getSubdomainFromUrl';
import {
  ConsentField,
  LABEL_KEY_BY_FIELD,
  USER_FIELD_BY_CONSENT_TYPE,
} from './consentRequirements';
import { usePrivacyConsentRequirements } from './usePrivacyConsentRequirements';

export type ConsentCheckbox = {
  name: ConsentField;
  label: string;
  optional?: boolean;
};

type Options = {
  /** Required by this user's location and not yet given. */
  outstandingConsents: ConsentType[];
  /** Offered on a first consent capture only: the stored value is write-only. */
  showMarketingOptIn: boolean;
};

const MARKETING_FIELD = 'marketingOptIn';

type CheckboxRow = { name: ConsentField; show?: boolean; optional?: boolean };

// Rendering and submitting both derive from this, so what was asked and what
// gets written cannot disagree.
export const useConsentsToCollect = ({
  outstandingConsents,
  showMarketingOptIn,
}: Options) => {
  const { t: translate } = useTranslation(['logged_out']);
  const subDomain = getSubdomainFromUrl();
  const { showEEAExplicitDataStorageConsent, loading } =
    usePrivacyConsentRequirements(subDomain);

  const toCheckbox = ({ name, optional }: CheckboxRow): ConsentCheckbox => ({
    name,
    label: translate(LABEL_KEY_BY_FIELD[name]),
    optional,
  });

  // Shares attribute 87 with the PIPL sensitive data consent, which is only
  // absent from the outstanding set once given — so 87 is already set.
  const showExplicitPrivacyConsent = outstandingConsents.length === 0;

  const checkboxes: CheckboxRow[] = [
    {
      name: 'explicitPrivacyConsentGiven',
      show: showExplicitPrivacyConsent,
    },
    {
      name: 'explicitEEADataStorageConsentGiven',
      show: showEEAExplicitDataStorageConsent,
    },
    {
      name: USER_FIELD_BY_CONSENT_TYPE[ConsentType.PIPL_PRIVACY_POLICY],
      show: outstandingConsents.includes(ConsentType.PIPL_PRIVACY_POLICY),
    },
    // Above the separate consents: a box after them reads as one of them.
    { name: MARKETING_FIELD, show: showMarketingOptIn, optional: true },
  ];

  const shownCheckboxes = checkboxes.filter(({ show }) => show).map(toCheckbox);

  const separateConsentCheckboxes = outstandingConsents
    .filter(consentType => consentType !== ConsentType.PIPL_PRIVACY_POLICY)
    .map(consentType =>
      toCheckbox({ name: USER_FIELD_BY_CONSENT_TYPE[consentType] }),
    );

  return {
    loading,
    checkboxes: shownCheckboxes,
    separateConsentCheckboxes,
    /** Submit these and nothing else. */
    shownFields: [...shownCheckboxes, ...separateConsentCheckboxes].map(
      ({ name }) => name,
    ),
  };
};
